import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import i18n from "i18next"

import { ScriptSchemaFeature } from "../../../types/generated/api/ScriptSchemaFeature"
import type { WorkflowConstantFeature } from "../../../types/generated/api/WorkflowConstantFeature"

type FeatureType = WorkflowConstantFeature.type | ScriptSchemaFeature.type

export const typeCastBasedOnType = (value: unknown, type: FeatureType): unknown => {
  switch (type.toLowerCase()) {
    case "number":
      return isNaN(Number(value)) ? 0 : Number(value)
    case "boolean":
      // Convert value to string, make it lowercase, and compare with "true"
      // This handles various boolean string representations ("True", "TRUE", "true")
      // Returns true only if the lowercase value exactly matches "true", false otherwise
      return String(value).toLowerCase() === "true"
    case "list":
      if (Array.isArray(value)) {
        return value.length === 0 ? [] : value
      } else {
        return value ? [value] : []
      }
    case "undefined":
      return null
    default:
      return `${value}`
  }
}

// Lightweight validation that only checks JSON structure and basic rules
const validateJsonStructure = (jsonText: string): void | Error => {
  if (!jsonText.trim()) {
    return // Empty JSON is valid
  }

  const parsed = JSON.parse(jsonText)

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error(i18n.t("tabularJsonTable.notifications.mustBeObject", { ns: "tables" }))
  }

  return
}

export const testJson = (jsonText: string, fallbackMessage: string): boolean => {
  try {
    validateJsonStructure(jsonText)
    return true
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error))

    NotificationUtils.toast(err?.toString() ?? fallbackMessage, {
      title: i18n.t("tabularJsonTable.notifications.invalidJson", { ns: "tables" }),
      severity: "error",
    })

    return false
  }
}

export const convertTableDataToJson = (data: { [key: string]: unknown }): string => {
  return JSON.stringify(data, null, 2)
}

/**
 * Configuration for the generic merge function.
 * @template TItem The type of objects in the array.
 * @template TType The type of the 'type' field (e.g., a string enum).
 */
export interface MergeOptions<TItem, TType> {
  keyField: keyof TItem // The property to use as the unique key (e.g., 'name').
  valueField: keyof TItem // The property to store the JSON value in (e.g., 'value').

  // OPTIONAL: The property to store the inferred type in.
  typeField?: keyof TItem

  // OPTIONAL: The boolean property that "locks" an item from updates.
  lockField?: keyof TItem
  lockValue?: boolean // The value that indicates the item is locked (default: true).

  // OPTIONAL: A function to infer the type from a given value.
  inferType?: (value: unknown) => TType

  // A function to create a new item. The 'type' is optional to accommodate
  // cases where inferType is not provided.
  createNewItem: (key: string, value: unknown, type?: TType) => TItem
}

/**
 * A generalized function to merge a JSON object with an existing array of items.
 */
export const mergeJsonWithArray = <TItem extends Record<string, unknown>, TType>(
  jsonText: string,
  existingItems: TItem[],
  options: MergeOptions<TItem, TType>,
): TItem[] => {
  const jsonObject = JSON.parse(jsonText)
  const jsonItemsMap = new Map<string, unknown>(Object.entries(jsonObject))

  const processedItems: TItem[] = []

  for (const item of existingItems) {
    const itemKey = item[options.keyField] as string

    if (jsonItemsMap.has(itemKey)) {
      const jsonValue = jsonItemsMap.get(itemKey)
      jsonItemsMap.delete(itemKey) // Mark as processed

      // If the item is "locked"
      if (options.lockField && item[options.lockField]) {
        // If value is locked, keep item as is.
        if (options.lockValue) {
          processedItems.push(item)
        } else {
          // update value
          processedItems.push({
            ...item,
            [options.valueField]: jsonValue,
            [options.typeField ?? ""]: options.inferType && options.inferType(jsonValue),
          })
        }
      } else {
        // Otherwise, update it with the new value and type.
        processedItems.push({
          ...item,
          [options.valueField]: jsonValue,
          [options.typeField ?? ""]: options.inferType && options.inferType(jsonValue),
        })
      }
    } else if (options.lockField && item[options.lockField]) {
      // Keep locked items even if they are not in the new JSON.
      processedItems.push(item)
    }
    // Unlocked items not in the new JSON are discarded.
  }

  // Add any completely new items from the JSON.
  const newItems = Array.from(jsonItemsMap.entries()).map(([key, value]) =>
    options.createNewItem(key, value, options.inferType && options.inferType(value)),
  )

  return [...newItems, ...processedItems]
}
